/**
 * Single source of truth for Thea's cookie / tracking consent POLICY: the
 * version string, the storage key, the shape of a stored consent receipt, and
 * the mapping from a user decision to Google Consent Mode v2 signals.
 *
 * ## The banner is GEO-GATED to California and Florida
 *
 * Visitors resolved to CA or FL are prompted and have their client-side tags
 * withheld until they accept. Everyone else is auto-granted (`not-required`)
 * and never sees the banner.
 *
 * The region is resolved SERVER-SIDE (`consentGeo.ts` -> the
 * `resolveConsentGeo` callable), not in the browser. thea-web is a static CRA
 * bundle on Firebase Hosting with no server render and no country header
 * reachable from the page, so the only client-only options were a third-party
 * geo-IP script — itself exactly the kind of third party we refuse to load
 * pre-consent — or the browser timezone. Timezone was tried and dropped: CA
 * and FL have no dedicated IANA zone, so covering them means gating all of
 * Pacific/Eastern/Central, and it fails in the WRONG direction because a
 * Californian on a VPN or with a skewed clock reads as ungated and gets
 * tracked.
 *
 * Asking the server is affordable only because Consent Mode v2 defaults=denied
 * already ships: the page starts fully withheld with no Pixel injected, so
 * nothing leaks while the answer is in flight.
 *
 * Every failure — network, timeout, an unparseable answer, no dataset —
 * resolves to "consent required", i.e. show the banner. Being wrong that way
 * shows a banner to someone who did not need one; being wrong the other way
 * tracks a Californian without consent, and only the second is a compliance
 * failure.
 *
 * Adding a state is a server-side change (`GATED_CLASSES` in
 * `consent_geo.py`); nothing here needs to move.
 *
 * ## Why the policy is a module separate from the store and the UI
 *
 * The same policy has to be applied in three places that can't import each
 * other:
 *
 *   1. the synchronous bootstrap in `public/index.html`, which must run BEFORE
 *      gtag.js loads (Consent Mode defaults are only honoured if they are set
 *      before the tag). That file can't import TypeScript, so it carries a
 *      hand-mirrored copy of the two constants below;
 *   2. this app-level runtime (`consentStore`), which owns the React state;
 *   3. the analytics wrappers (`gaPixel` / `metaPixel`), which ask "may I fire?"
 *
 * `__tests__/consentBootstrapParity.test.ts` greps `public/index.html` and
 * fails if the mirrored constants drift. That's DRY-by-enforcement — the only
 * kind available across the HTML/TS boundary.
 *
 * Server-side Meta CAPI is deliberately NOT gated anywhere; see `metaCapi.ts`
 * and the note in `metaPixel.ts`.
 */

/**
 * Bumping this re-prompts every visitor whose stored receipt carries an older
 * version. Bump it whenever the set of vendors, the categories, or the banner
 * copy materially changes — a stale receipt is not valid consent for a new
 * disclosure.
 *
 * MIRRORED in `public/index.html`; the parity test enforces it.
 */
export const CONSENT_VERSION = '2026-09-04';

/**
 * localStorage key holding the serialized `ConsentReceipt`.
 *
 * MIRRORED in `public/index.html`; the parity test enforces it.
 */
export const CONSENT_STORAGE_KEY = 'thea.consent';

/** What the visitor has decided (or hasn't). */
export type ConsentStatus =
  /** No decision yet. Tags withheld. The banner shows only once geo has said
   *  this visitor is in a gated state (or geo failed) — see `consentGeo.ts`. */
  | 'pending'
  /** Explicitly accepted by the visitor. Tags fire. */
  | 'granted'
  /** Explicitly declined by the visitor. Tags stay off; no banner. */
  | 'denied'
  /**
   * Geo says this visitor is outside the gated states, so consent is not
   * required of them. Tags fire.
   *
   * A SEPARATE status from `granted` on purpose. `granted` means a human
   * clicked Accept; this means we never asked. Collapsing them would let a
   * later audit read an auto-grant as a recorded consent, and it would let a
   * geo fact get written into the durable receipt — which is why this status
   * is never persisted to localStorage (see `consentStore`).
   */
  | 'not-required';

/**
 * The record we persist when the visitor decides. Mirrors the "Consent
 * records" row of the launch gap analysis (2026-08-19): `{type, version,
 * acceptedAt, ip, userAgent}`.
 *
 * `ip` is deliberately absent — a browser cannot read its own egress IP
 * without calling a third party, and calling a third party pre-consent is
 * exactly what this feature exists to prevent. Stamping the IP is a
 * server-side follow-up (see the module docblock in `consentStore.ts`).
 */
export interface ConsentReceipt {
  /** Discriminator, so a future ToS/email receipt can share the collection. */
  type: 'cookie_banner';
  /** The `CONSENT_VERSION` in force when the decision was made. */
  version: string;
  /** The decision itself. `pending` is never persisted. */
  status: 'granted' | 'denied';
  /** ISO-8601 timestamp of the click. */
  acceptedAt: string;
  /** Verbatim `navigator.userAgent` at decision time. */
  userAgent: string;
}

/**
 * Google Consent Mode v2 signal names we set. The three "essential" signals
 * (`functionality_storage`, `personalization_storage`, `security_storage`)
 * stay granted in every state: they cover strictly-necessary storage, which
 * no consent regime requires opt-in for.
 */
export interface ConsentModeSignals {
  ad_storage: 'granted' | 'denied';
  ad_user_data: 'granted' | 'denied';
  ad_personalization: 'granted' | 'denied';
  analytics_storage: 'granted' | 'denied';
  functionality_storage: 'granted';
  personalization_storage: 'granted';
  security_storage: 'granted';
}

/**
 * Map a status to Consent Mode v2 signals.
 *
 * Only an explicit `granted` unlocks the four opt-in signals. `pending` and
 * `denied` both deny — a visitor who has not decided is treated exactly like
 * one who declined, which is the point of "don't fire until accept".
 */
export function signalsFor(status: ConsentStatus): ConsentModeSignals {
  const value = allowsTags(status) ? 'granted' : 'denied';
  return {
    ad_storage: value,
    ad_user_data: value,
    ad_personalization: value,
    analytics_storage: value,
    functionality_storage: 'granted',
    personalization_storage: 'granted',
    security_storage: 'granted',
  };
}

/**
 * The two states in which tags may fire: the visitor accepted, or they are
 * outside the gated states and were never asked.
 */
function allowsTags(status: ConsentStatus): boolean {
  return status === 'granted' || status === 'not-required';
}

/** True when GA4 events may be emitted by app code. */
export function analyticsAllowed(status: ConsentStatus): boolean {
  return allowsTags(status);
}

/**
 * True when the Meta Pixel may be loaded and fired.
 *
 * Identical to `analyticsAllowed` today. It is a separate predicate on
 * purpose: GA4 and the Pixel are gated by the same banner but by different
 * mechanisms (Consent Mode vs. don't-load), and a future banner with
 * per-category toggles would split these two without touching call sites.
 */
export function advertisingAllowed(status: ConsentStatus): boolean {
  return allowsTags(status);
}

/**
 * Parse a stored receipt. Returns null for anything malformed, from a
 * different `CONSENT_VERSION`, or of an unknown status — all of which mean
 * "no valid decision on file", i.e. re-prompt and keep tags off.
 */
export function parseReceipt(raw: string | null): ConsentReceipt | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<ConsentReceipt>;
    if (parsed.type !== 'cookie_banner') return null;
    if (parsed.version !== CONSENT_VERSION) return null;
    if (parsed.status !== 'granted' && parsed.status !== 'denied') return null;
    if (typeof parsed.acceptedAt !== 'string') return null;
    return {
      type: 'cookie_banner',
      version: parsed.version,
      status: parsed.status,
      acceptedAt: parsed.acceptedAt,
      userAgent: typeof parsed.userAgent === 'string' ? parsed.userAgent : '',
    };
  } catch {
    return null;
  }
}

/** Build the receipt for a fresh decision. */
export function buildReceipt(status: 'granted' | 'denied'): ConsentReceipt {
  return {
    type: 'cookie_banner',
    version: CONSENT_VERSION,
    status,
    acceptedAt: new Date().toISOString(),
    userAgent:
      typeof navigator !== 'undefined' && typeof navigator.userAgent === 'string'
        ? navigator.userAgent
        : '',
  };
}
